import type { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { Global } from "@emotion/react";
import {
  H2,
  H3,
  H4,
  Callout,
  Link,
  Text,
  ScreenReaderText,
  IconWorldHug,
  IconLadders,
  IconInclusivity,
  IconHoldingHands,
  IconEyesLookingLeft,
  IconBoltBrush,
  IconHat,
  SiteContainer,
} from "atoms";
import { Head } from "features/misc";

import inclusiverseHeaderSVG from "assets/images/step-into-the-inclusiverse_sparkly.svg";
import tokenomicsSVG from "assets/images/roadmap_tokenomics.svg";
import mirrorSVG from "assets/images/roadmap_merch.svg";

import { BackToTop, JumpNav } from "./JumpNavigation";

const globalCSS = (theme: Theme) => ({
  "html,body": {
    background: theme.colors.yellow,
  },
});

const navItems = [
  {
    id: "diversity",
    label: "Diversity",
  },
  {
    id: "equity",
    label: "Equity",
  },
  {
    id: "inclusion",
    label: "Inclusion",
  },
  {
    id: "belonging",
    label: "Belonging",
  },
  {
    id: "accessibility",
    label: "Accessibility",
  },
];

function DeiInitiatives() {
  return (
    <Main>
      <Head title="DEI Initiatives" />
      <Global styles={globalCSS} />

      <SiteContainer>
        <Header>
          <ScreenReaderText>
            <h1>Diversity, Equity, and Inclusivity Initiatives</h1>
            <p>Step Into the Inclusiverse</p>
          </ScreenReaderText>
          <img
            src={inclusiverseHeaderSVG}
            alt="Step Into the Inclusiverse"
            aria-hidden="true"
          />

          <Text size="md">
            Inclusion is important to us at HUG. Aside from working directly to
            elevate and spotlight creators and founders from marginalized
            communities, we hope to further these efforts across Web3 as a whole
            so that it can be a space where everyone truly&nbsp;belongs.
          </Text>
        </Header>

        <Section>
          <SectionHeading>
            <SIconBoltBrush aria-hidden="true" />
            <SIconHat aria-hidden="true" />
            <IconMirror src={mirrorSVG} aria-hidden="true" />
            <H2 size="h1">Our Community</H2>
          </SectionHeading>
          <CommunityStats>
            <li>
              <Text>
                <strong>70%</strong> Women and non-binary
              </Text>
            </li>
            <li>
              <Text>
                <strong>52%</strong> Live outside the United States
                across&nbsp;48&nbsp;countries
              </Text>
            </li>
            <li>
              <Text>
                <strong>16%</strong> LGBTQIA+
              </Text>
            </li>
            <li>
              <Text>
                <strong>57%</strong>{" "}
                <span>
                  Black, indigenous, people of color&nbsp;(
                  <abbr title="Black, Indigenous, and people of color">
                    BIPOC
                  </abbr>
                  )
                </span>
              </Text>
            </li>
            <li>
              <Text>
                <strong>41%</strong> Do not speak English as
                a&nbsp;first&nbsp;language
              </Text>
            </li>
            <li>
              <Text>
                <strong>10%</strong> Have a disability
              </Text>
            </li>
          </CommunityStats>
        </Section>

        <Section>
          <SectionHeading>
            <H2 size="h1">Where We Are Today</H2>
            <Text>
              We believe that any and all{" "}
              <abbr title="Diversity, Equity, Inclusion, Belonging, and Accessibility">
                DEIBA
              </abbr>{" "}
              efforts have to start with ourselves. The following shows us where
              we stand after our first community DEIBA survey, which was
              conducted in June&nbsp;2022 with HUG&nbsp;Passholders.
            </Text>

            <JumpNav items={navItems} />
          </SectionHeading>

          <DiversityCallout>
            <H3 size="xxl" id={navItems[0].id}>
              Diversity
              <IconWorldHug aria-hidden="true" />
            </H3>
            <Text>
              Presence of different and multiple, individual and collective,
              identities that makes up a community.
            </Text>
            <Figure>
              <DeiBarChartHeading>
                “Time is invested into building diverse teams.”
              </DeiBarChartHeading>
              <DeiBarChart hug={90} web3={36} />
              <figcaption>
                90% agree that HUG invests time and energy into building diverse
                teams, versus 36% who agree that Web3 does.
              </figcaption>
            </Figure>
            <BackToTop />
          </DiversityCallout>

          <DiversityCallout>
            <H3 size="xxl" id={navItems[1].id}>
              Equity
              <IconLadders aria-hidden="true" />
            </H3>
            <Text>
              Breaking down barriers to ensure everyone has the same
              opportunities to participate and thrive.
            </Text>
            <Figure>
              <DeiBarChartHeading>
                “I have equal opportunities to participate and benefit.”
              </DeiBarChartHeading>
              <DeiBarChart hug={80} web3={43} />
              <figcaption>
                80% agree that they have equal opportunities to participate and
                benefit from HUG, versus 43% who agree that they have equal
                opportunities to participate and benefit from Web3.
              </figcaption>
            </Figure>
            <BackToTop />
          </DiversityCallout>

          <DiversityCallout>
            <H3 size="xxl" id={navItems[2].id}>
              Inclusion
              <IconInclusivity aria-hidden="true" />
            </H3>
            <Text>
              Creating an environment for everyone to feel welcomed, respected,
              supported and valued.
            </Text>
            <Figure>
              <DeiBarChartHeading>
                “I feel respected and valued.”
              </DeiBarChartHeading>
              <DeiBarChart hug={85} web3={42} />
              <figcaption>
                80% agree that they have equal opportunities to participate and
                benefit from HUG, versus 43% who agree that they have equal
                opportunities to participate and benefit from Web3.
              </figcaption>
            </Figure>
            <BackToTop />
          </DiversityCallout>

          <DiversityCallout>
            <H3 size="xxl" id={navItems[3].id}>
              Belonging
              <IconHoldingHands aria-hidden="true" />
            </H3>
            <Text>
              Ensuring that everyone is secure, recognized, affirmed, and
              accepted equally.
            </Text>
            <Figure>
              <DeiBarChartHeading>
                “I feel a sense of belonging.”
              </DeiBarChartHeading>
              <DeiBarChart hug={79} web3={49} />
              <figcaption>
                79% feel a sense of belonging in HUG, versus 49% who feel a
                sense of belonging in Web3.
              </figcaption>
            </Figure>
            <BackToTop />
          </DiversityCallout>

          <DiversityCallout>
            <H3 size="xxl" id={navItems[4].id}>
              Accessibility
              <IconEyesLookingLeft aria-hidden="true" />
            </H3>
            <Text>
              Ensuring that everyone is secure, recognized, affirmed, and
              accepted equally.
            </Text>
            <Figure>
              <DeiBarChartHeading>
                “HUG&rsquo;s communications and announcements are
                easy&nbsp;to&nbsp;understand.”
              </DeiBarChartHeading>
              <DeiBarChart hug={85} />
              <figcaption>
                85% agree that HUG&rsquo;s communications and announcements are
                easy&nbsp;to&nbsp;understand.
              </figcaption>
            </Figure>
            <BackToTop />
          </DiversityCallout>
        </Section>

        <Section>
          <SectionHeading>
            <H2 size="h1">
              HUG&rsquo;s Guide to <br />
              Inclusive Language
              <GuideSVG src={tokenomicsSVG} aria-hidden="true" />
            </H2>
            <Text>
              Have questions? Join the conversation on Discord at
              #dei-discussion.
            </Text>
            <Link
              variant="gradient"
              arrow="dr"
              href="https://discord.com/invite/thehugxyz"
            >
              Visit our Discord
            </Link>
          </SectionHeading>
          <LanguageCallout>
            <H3 size="md">Race and Ethnicity</H3>
            <Text as="ul">
              <li>Capitalize and do not pluralize Black.</li>
              <li>
                Only identify a person&rsquo;s race, ethnicity, or national
                origin if relevant and always defer to how they want to
                self-identify.
              </li>
            </Text>

            <H3 size="md">Gender</H3>
            <Text as="ul">
              <li>
                When referring to a group of people, use non-gendered
                identifiers (e.g. folks, everybody, or Huggers).
              </li>
              <li>
                When referring to a specific individual, always defer to how
                they self-identify and if not, use gender-neutral pronouns such
                as they/them.
              </li>
              <li>
                Accidentally misgendered someone? Acknowledge and apologize
                gently, then move on without feeling the need to make a scene.
              </li>
            </Text>

            <H3 size="md">Identity</H3>
            <Text>Use LBGTQIA+ or most current.</Text>

            <H3 size="md">
              People with disabilities and/or chronic conditions
            </H3>
            <Text>
              When referring to people with disabilities, respect individual
              preferences. Some communities prefer to emphasize the person (e.g.
              “A person with a disability” not “disabled person”), while others
              have a strong preference for identity-first language (e.g. “I am
              autistic” not “I have autism”). The important thing is to listen
              to how individuals refer to themselves, and try to honor that if
              you can.
            </Text>

            <H3 size="md">Other</H3>
            <Text>
              Research the origin of any idiom before using it in copy, some
              examples of non-inclusive vocabulary include terms like whitelist,
              frens, and even tribe.
            </Text>
          </LanguageCallout>
        </Section>
      </SiteContainer>
    </Main>
  );
}

const Main = styled.main(({ theme }) => ({
  background: [theme.colors.yellow, theme.gradients.radial2],
  overflow: "hidden",
}));
const Section = styled.section({
  margin: "4rem auto",
  "&+&": {
    marginTop: "12rem",
  },
});
const Header = styled.header(({ theme }) => ({
  padding: "0 0 1rem",
  position: "relative",
  textAlign: "center",
  maxWidth: "780px",
  margin: "0 auto",
  img: {
    width: "580px",
    marginBottom: "2rem",
    // marginLeft and maxWidth = 100% to visually center the text in the image
    marginLeft: "3%",
    maxWidth: "97%",
  },
  "@media (min-width: 600px)": {
    paddingBottom: "3rem",
  },
}));
const SectionHeading = styled.div({
  position: "relative",
  margin: "0 auto 3rem",
  maxWidth: "800px",
  textAlign: "center",
  h2: {
    maxWidth: "max-content",
    marginLeft: "auto",
    marginRight: "auto",
    position: "relative",
  },
});

const CommunityStats = styled.ul(({ theme }) => ({
  display: "grid",
  gap: "1rem 4rem",
  listStyle: "none",
  margin: "3rem auto",
  maxWidth: "780px",
  padding: 0,
  "@media (min-width: 700px)": {
    gridTemplateColumns: "1fr 1fr",
  },
  "> *::before": {
    content: `"\\200B"`,
  },
  li: {
    margin: 0,
  },
  p: {
    alignItems: "flex-start",
    display: "flex",
    fontSize: theme.fontSizes.md,
    gap: ".5em",
    margin: 0,
  },
  strong: {
    ...theme.fonts.display,
    fontSize: "115%",
    transform: "translate(0, -5%)",
  },
}));

const SCallout = styled(Callout)(({ theme }) => ({
  background: "transparent",
  padding: "3rem 0",
  maxWidth: "800px",
  marginLeft: "auto",
  marginRight: "auto",
  textAlign: "center",
  "@media (min-width: 800px)": {
    padding: "3rem 2rem",
  },
}));
const DiversityCallout = styled(SCallout)(({ theme }) => ({
  h3: {
    ...theme.fonts.display,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
  },
}));

const LanguageCallout = styled(SCallout)(({ theme }) => ({
  backgroundColor: "rgba(255,255,255,.25)",
  borderRadius: theme.borderRadius.lg,
  padding: "2rem 1rem",
  textAlign: "left",
  "li+li": {
    marginTop: ".5em",
  },
  "@media (min-width: 420px)": {
    padding: "3rem 2rem",
  },
}));
const Figure = styled.figure(({ theme }) => ({
  backgroundColor: "rgba(255,255,255,.25)",
  borderRadius: theme.borderRadius.lg,
  padding: "2rem 1rem",
  margin: "2rem 0 1rem",
  "@media (min-width: 420px)": {
    padding: "3rem 2rem",
  },
  figcaption: {
    fontSize: theme.fontSizes.xs,
    textAlign: "center",
  },
  "&+a": {
    opacity: ".8",
    fontSize: "15px",
  },
}));
const DeiBarChartHeading = styled(H4)(({ theme }) => ({
  fontSize: theme.fontSizes.md,
  fontStyle: "italic",
  textAlign: "center",
}));

interface BarChartProps {
  hug: number;
  web3?: number;
}

const BarChart = styled.dl(({ theme }) => ({
  ...theme.fonts.display,
  justifyContent: "space-evenly",
  alignItems: "flex-end",
  display: "flex",
  margin: "2rem 0 3.5rem 0",
  padding: "0 1rem",
  position: "relative",
  height: "calc(150px + 10vw)",
}));

const BarChartGrid = styled.div({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: "-3px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  pointerEvents: "none",
  "span,strong": {
    height: 1,
    backgroundColor: "black",
    position: "relative",
    zIndex: 1,
  },
  strong: {
    height: 3,
    zIndex: 3,
  },
});

interface BarProps {
  height: number;
}

const BarHug = styled.div<BarProps>(({ theme, height }) => ({
  alignItems: "center",
  display: "flex",
  fontSize: theme.fontSizes.h3,
  justifyContent: "center",
  maxWidth: "150px",
  marginBottom: "-1px",
  opacity: ".95",
  position: "relative",
  width: "45%",
  zIndex: 2,
  backgroundColor: theme.colors.primary,
  height: `${height}%`,
  "dt,dd": {
    padding: 0,
    margin: 0,
    lineHeight: "1",
  },
  dt: {
    position: "absolute",
    top: "calc(100% + .5em)",
    left: 0,
    right: 0,
    textAlign: "center",
    textTransform: "uppercase",
  },
}));

const BarWeb3 = styled(BarHug)(({ height }) => ({
  backgroundColor: "#d4d4d4",
  height: `${height}%`,
  dt: {
    textTransform: "none",
  },
}));

function DeiBarChart({ hug, web3 }: BarChartProps) {
  return (
    <BarChart>
      <BarChartGrid>
        <span />
        <span />
        <span />
        <span />
        <strong />
      </BarChartGrid>

      <BarHug height={hug}>
        <dt>Hug</dt>
        <dd>
          {hug}% <br />
          <small>agree</small>
        </dd>
      </BarHug>

      {web3 && (
        <BarWeb3 height={web3}>
          <dt>Web3</dt>
          <dd>
            {web3}% <br />
            <small>agree</small>
          </dd>
        </BarWeb3>
      )}
    </BarChart>
  );
}

// icons
const SIconBoltBrush = styled(IconBoltBrush)({
  marginBottom: "-2rem",
  transform: "scale(.66) translate(-10%, 0%)",
  "@media (min-width: 600px)": {
    left: 0,
    marginBottom: "0",
    position: "absolute",
    top: 0,
    transform: "scale(.66) translate(-80%, -33%)",
  },
  "@media (min-width: 800px)": {
    transform: "translate(-80%, -33%)",
  },
});
const SIconHat = styled(IconHat)({
  marginBottom: "-2rem",
  transform: "scale(.66) translate(10%, 30%)",
  "@media (min-width: 600px)": {
    left: 0,
    marginBottom: "0",
    position: "absolute",
    top: 0,
    transform: "scale(.66) translate(32%, -65%)",
  },
  "@media (min-width: 800px)": {
    transform: "translate(32%, -65%)",
  },
});
const IconMirror = styled.img({
  "@media (max-width: 599px)": {
    display: "none",
  },
  position: "absolute",
  top: "50%",
  transform: "scale(1.3)",
  right: 0,
  "@media (min-width: 800px)": {
    top: "120%",
    transform: "scale(2) translate(100%, 0)",
  },
});
const GuideSVG = styled.img({
  position: "absolute",
  top: 0,
  right: "50%",
  height: "auto",
  transform: "translate(50%, -115%)",
  width: "calc(60px + 3vw)",
  "@media (min-width: 440px)": {
    right: 0,
    transform: "translate(50%, -60%)",
  },
});
export { DeiInitiatives };
